import { useEffect, useMemo, useRef, useState } from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"

type SearchableSelectProps = {
  label: string
  options: readonly string[]
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
}

type SearchableMultiSelectProps = {
  label: string
  options: readonly string[]
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
}

function useOutsideClose(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener("mousedown", handle)
    return () => document.removeEventListener("mousedown", handle)
  }, [open, onClose])
  return ref
}

export function SearchableSelect({
  label,
  options,
  value,
  onChange,
  placeholder = "Search…",
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const ref = useOutsideClose(open, () => setOpen(false))
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return [...options]
    return options.filter((o) => o.toLowerCase().includes(q))
  }, [options, query])

  return (
    <fieldset className="rounded border border-[#555] px-4 pb-3 pt-1">
      <legend className="px-1 text-sm text-[#aaa]">{label}</legend>
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-2 py-1 text-left text-sm text-white"
        >
          <span className={value ? "" : "text-[#666]"}>{value || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-[#888]" aria-hidden />
        </button>
        {open ? (
          <div className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-md border border-[#555] bg-[#2d2d2d] shadow-lg">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full border-b border-[#555] bg-[#252525] px-3 py-2 text-sm text-white outline-none placeholder:text-[#666]"
            />
            <ul className="max-h-48 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-[#888]">No matches</li>
              ) : (
                filtered.map((opt) => (
                  <li key={opt}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm text-white hover:bg-[#3a3a3a]"
                      onClick={() => {
                        onChange(opt)
                        setOpen(false)
                        setQuery("")
                      }}
                    >
                      {opt}
                      {value === opt ? <Check className="h-4 w-4 text-[#8b9aff]" aria-hidden /> : null}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : null}
      </div>
    </fieldset>
  )
}

export function SearchableMultiSelect({
  label,
  options,
  values,
  onChange,
  placeholder = "Search countries…",
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const ref = useOutsideClose(open, () => setOpen(false))
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return [...options]
    return options.filter((o) => o.toLowerCase().includes(q))
  }, [options, query])

  function toggle(opt: string) {
    if (values.includes(opt)) onChange(values.filter((v) => v !== opt))
    else onChange([...values, opt])
  }

  return (
    <fieldset className="rounded border border-[#555] px-4 pb-3 pt-1">
      <legend className="px-1 text-sm text-[#aaa]">{label}</legend>
      <div ref={ref} className="relative space-y-2">
        {values.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {values.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded-md border border-[#555] bg-[#3a3a3a] px-2 py-0.5 text-xs text-white"
              >
                {v}
                <button
                  type="button"
                  aria-label={`Remove ${v}`}
                  onClick={() => onChange(values.filter((x) => x !== v))}
                  className="text-[#aaa] hover:text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-2 py-1 text-left text-sm text-white"
        >
          <span className="text-[#666]">{placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-[#888]" aria-hidden />
        </button>
        {open ? (
          <div className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-md border border-[#555] bg-[#2d2d2d] shadow-lg">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full border-b border-[#555] bg-[#252525] px-3 py-2 text-sm text-white outline-none placeholder:text-[#666]"
            />
            <ul className="max-h-48 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-[#888]">No matches</li>
              ) : (
                filtered.map((opt) => {
                  const selected = values.includes(opt)
                  return (
                    <li key={opt}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm text-white hover:bg-[#3a3a3a]"
                        onClick={() => toggle(opt)}
                      >
                        {opt}
                        {selected ? <Check className="h-4 w-4 text-[#8b9aff]" aria-hidden /> : null}
                      </button>
                    </li>
                  )
                })
              )}
            </ul>
          </div>
        ) : null}
      </div>
    </fieldset>
  )
}
